import { ContentCopy as ContentCopyIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Card, CardActions, CardContent, CardMedia, CircularProgress, FormControl, IconButton, InputLabel, MenuItem, Select, Switch, TextField, Typography } from "@mui/material";
import { apiRequest } from 'api-client';
import { capitalize, debounce } from 'lodash';
import { useCallback, useEffect, useState } from "react";
import { RecordingIndicator } from './RecordingIndicator';
import { VideoJSPlayer } from './VideoJSPlayer';


export const StreamConsole = () => {
  const [streams, setStreams] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [itemLoading, setItemLoading] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('broadcasting');
  const [showPreview, setShowPreview ] = useState<boolean>(false);

  const fetchStreams = useCallback((search: string, status = 'broadcasting') => {
    setLoading(true);
    apiRequest.get(`/antmedia/streams`, { params: {search, status} })
      .then(streamsData => {
        if (Array.isArray(streamsData)) {
          const streamsMap = streamsData.reduce((acc, stream) => {
            acc[stream.streamId] = stream;
            stream['url'] = streams[stream.streamId]?.url || stream.url; // Preserve existing URL if available
            return acc;
          }, {} as Record<string, any>);
          setStreams(streamsMap);
        } else {
          setStreams({});
        }
      })
      .catch(error => {
        console.error("Error fetching streams:", error);
      }).finally(() => {
        setLoading(false);
      });
  }, [streams]);


  const startRecording = useCallback((streamId: string) => {
    setItemLoading((prev) => ({ ...prev, [streamId]: true }));
    apiRequest.put(`/antmedia/streams/${streamId}`, { action: 'startRecording' })
      .then((data) => {
        if (data.success) {
          setStreams((prevStreams) => ({
            ...prevStreams,
            [streamId]: {
              ...prevStreams[streamId],
              mp4Enabled: 1,
            },
          }));
        }
      })
      .catch(error => {
        console.error("Error starting recording:", error);
      }).finally(() => {
        setItemLoading((prev) => ({ ...prev, [streamId]: false }));
      });
  }, []);

  const stopRecording = useCallback((streamId: string) => {
    setItemLoading((prev) => ({ ...prev, [streamId]: true }));
    apiRequest.put(`/antmedia/streams/${streamId}`, { action: 'stopRecording' })
      .then((data) => {
        if (data.success) {
          setStreams((prevStreams) => ({
            ...prevStreams,
            [streamId]: {
              ...prevStreams[streamId],
              mp4Enabled: -1,
            },
          }));
        }
      })
      .catch(error => {
        console.error("Error stopping recording:", error);
      }).finally(() => {
        setItemLoading((prev) => ({ ...prev, [streamId]: false }));
      });
  }, []);

  const copyStreamUrl = useCallback((url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      console.log('Stream URL copied to clipboard');
    }).catch(error => {
      console.error('Error copying URL to clipboard:', error);
    });
  }, []);

  const onSearchChange = useCallback(debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchTerm(searchValue);
    fetchStreams(searchValue, statusFilter);
  }, 300), [fetchStreams]);

  // Initial load
  useEffect(() => {
    fetchStreams(searchTerm, statusFilter);
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      {loading &&
        <Box sx={{ position: 'absolute', top: 0, right: 0, left: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
          <CircularProgress />
        </Box>
      }
      <Box mb={2} display="flex">
        <Box display="flex" alignItems="center" gap={1}>
          <Box>
            Refresh:<IconButton color="primary" onClick={() => fetchStreams(searchTerm)}><RefreshIcon /></IconButton>
          </Box>
          <Box>
            Show stream preview:<Switch checked={showPreview} onChange={(e) => setShowPreview(e.target.checked)} />
          </Box>
        </Box>
        <Box ml="auto">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                fetchStreams(searchTerm, e.target.value);
              }}
              value={statusFilter}
            >
              <MenuItem value="broadcasting">Broadcasting</MenuItem>
              <MenuItem value="created">Created</MenuItem>
              <MenuItem value="finished">Finished</MenuItem>
              <MenuItem value="">All</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box ml={1}>
          <TextField 
            label="Search" 
            variant="outlined" 
            size="small"
            name="room-token"
            autoComplete="on"
            onChange={onSearchChange}
          />
        </Box>
      </Box>
      {Object.keys(streams).length === 0 ? (
        <p>No active streams</p>
      ) : (
        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))',
            gap: 2,
          }}
        >
          {Object.values(streams).map(stream => (
            <Card key={stream.streamId} variant="outlined">
              <CardMedia
                component="div"
                sx={{ position: 'relative', paddingTop: showPreview ? '56.25%' : 0 }}
              >
                {showPreview &&
                  <VideoJSPlayer src={stream.url} />
                }
                {stream.mp4Enabled === 1 && showPreview && (
                  <RecordingIndicator />
                )}
              </CardMedia>
              <CardContent sx={{display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between', marginBottom: 0}}>
                <Typography variant="h6" component="div">
                  {stream.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {capitalize(stream.status)}
                </Typography>
              </CardContent>
              <CardActions>
                {stream.mp4Enabled !== 1 && (
                  <LoadingButton loading={itemLoading[stream.streamId] || false} variant="contained" size="small" color="primary" onClick={() => startRecording(stream.streamId)}>Start Recording</LoadingButton>
                )}
                {stream.mp4Enabled === 1 && (
                  <LoadingButton loading={itemLoading[stream.streamId] || false} variant="contained" size="small" color="error" onClick={() => stopRecording(stream.streamId)}>Stop Recording</LoadingButton>
                )}
                <Button 
                  variant="outlined" 
                  size="small" 
                  startIcon={<ContentCopyIcon />}
                  onClick={() => copyStreamUrl(stream.url)}
                  sx={{ ml: 'auto' }}
                >
                  Copy URL
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}